
import {
  Component, Prop, Mixins,
} from 'vue-property-decorator';
import ServiceLevel from '@/features/shipment-history/components/tableColumns/ServiceLevel.vue';
import Created from '@/features/shipment-history/components/tableColumns/Created.vue';
import ShipStatus from '@/features/shipment-history/components/tableColumns/ShipStatus.vue';
import ShipTo from '@/features/shipment-history/components/tableColumns/ShipTo.vue';
import ShipFrom from '@/features/shipment-history/components/tableColumns/ShipFrom.vue';
import PackageType from '@/features/shipment-history/components/tableColumns/PackageType.vue';
import LastScan from '@/features/shipment-history/components/tableColumns/LastScan.vue';
import Carrier from '@/features/shipment-history/components/tableColumns/Carrier.vue';
import { HistoryItemType, HistoryPackageItem } from '@/features/shipment-history/types';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import * as ShipmentTypes from '@/features/shipment/types';
import ResponsivenessMixin from '@/features/common/mixins/responsivenessMixin';

@Component({
  components: {
    ServiceLevel,
    Created,
    ShipStatus,
    ShipTo,
    ShipFrom,
    PackageType,
    LastScan,
    Carrier,
  },
})
export default class HistoryTableRow extends Mixins(ResponsivenessMixin) {
  @Prop({ required: true }) item!: HistoryItemType;

  @Prop({ required: true }) columnsWidths!: number[];

  @Prop({ default: false }) isSelected!: boolean;

  @Prop({ default: false }) isOpened!: boolean;

  private packagesExpanded = !this.isMoreThan5Packages;

  private tabletTableColumns: string[] = [
    'Carrier',
    'ServiceLevel',
    'ShipFrom',
    'ShipTo',
    'PackageType',
    'Created',
    'LastScan',
  ];

  private mobileTableColumns: string[] = [
    'Carrier',
    'ServiceLevel',
    'PackageType',
    'Created',
    'ShipTo',
  ]

  get classes(): { [key: string]: boolean } {
    const cl = 'history-table-row';

    return {
      [cl]: true,
      [`${cl}--deleted`]: this.item.deleted,
      [`${cl}--opened`]: this.isOpened,
    };
  }

  get tableColumns(): string[] {
    return this.$store.getters.getHistoryTableColumns;
  }

  get filteredTableColumns(): string[] {
    if (this.isMobile) {
      return this.mobileTableColumns;
    }

    if (this.isTablet) {
      return this.tabletTableColumns;
    }

    return this.tableColumns;
  }

  get isMoreThan5Packages(): boolean {
    return this.item.packages.length > 5;
  }

  get visiblePackages(): HistoryPackageItem[] {
    if (!this.isMoreThan5Packages) {
      return this.item.packages;
    }

    const exceptionPackages = this.item.packages
      .filter((p) => p.shipStatus === ShipmentTypes.ShipStatus.Exception);
    const collapsedPackages: HistoryPackageItem[] = [...exceptionPackages];

    if (collapsedPackages.length < 5) {
      const qtyToAdd = 5 - collapsedPackages.length;
      const packagesToAdd = this.item.packages
        .filter((p) => p.shipStatus !== ShipmentTypes.ShipStatus.Exception)
        .slice(0, qtyToAdd);

      collapsedPackages.push(...packagesToAdd);
    }

    return collapsedPackages;
  }

  get invisiblePackages(): HistoryPackageItem[] {
    return this.item.packages.filter((p) => !this.visiblePackages.includes(p));
  }

  get visibleScans(): string[] {
    return this.visiblePackages.map((p) => p.lastScan);
  }

  get invisibleScans(): string[] {
    return this.invisiblePackages.map((p) => p.lastScan);
  }

  get visibleStatuses(): string[] {
    return this.visiblePackages.map((p) => p.shipStatus);
  }

  get invisibleStatuses(): string[] {
    return this.invisiblePackages.map((p) => p.shipStatus);
  }

  get isMobile(): boolean {
    return this.$vuetify.breakpoint.mdAndDown;
  }

  private setSelectedItem(): void {
    this.$store.commit('setSelectedHistoryItem', { ...this.item });
  }

  private openDetailsPage(): void {
    const { href } = this.$router.resolve(
      {
        name: 'history-details',
        query: {
          id: this.item.trackingNumber,
          trackingNumber: this.item.packages[0].carrierTrackingNumber,
        },
      },
    );

    window.open(href, '_blank');
  }

  private openAsideDetails(tab: string, carrierTrackingNumber: string): void {
    const vars = {
      tab,
      carrierTrackingNumber: carrierTrackingNumber || this.item.packages[0].carrierTrackingNumber,
      trackingNumber: this.item.trackingNumber,
    };

    eventBus.$emit(eventNames.OPEN_HISTORY_DETAILS, vars);
  }

  private getColumnStyle(idx: number): { [key: string]: string } | null {
    if (this.isTablet) return null;

    return {
      maxWidth: `${this.columnsWidths[idx]}px`,
      width: '100%',
    };
  }

  private togglePackagesExpand(): void {
    this.packagesExpanded = !this.packagesExpanded;
  }
}
